﻿import React, { useContext, useState, useEffect, useRef } from 'react';
import Context, { context_type } from '@Util/Context';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { generate_unique_id } from '@Util/Utils';
import {
    Icon_Edit,
    Icon_Arrow_Up,
    Icon_Arrow_Down,
    Icon_Clock,
    Icon_Trash,
    Icon_Split
} from '@Elements/ImageElements/Icons';

import {
    Context_Menu_Types,
    Time_Log_Entry
} from '@Util/Types';

export default function ContextMenu(props: Context_Menu_Types): JSX.Element {
    const [t] = useTranslation();
    const {
        weeks_time_log,
        set_prompt_information
    }: context_type = useContext(Context);
    const [is_visible, set_is_visible] = useState<boolean>(false);

    const context_menu_wrapper: any = useRef();
    const timelog_object: Time_Log_Entry = props?.entry_id?.length >= 1 ? weeks_time_log.find((o: Time_Log_Entry) => props?.entry_id === o.id.toString()) : null;

    useEffect(() => {
        document.addEventListener("mousedown", handle_blur_click);
        return () => {
            document.removeEventListener("mousedown", handle_blur_click);
        };
    }, []);

    useEffect(() => {
        if (props?.entry_id?.length >= 1) {
            set_is_visible(true);
        } 
    }, [props]);

    function handle_edit_mode_click(e): void {
        e.preventDefault();
        props?.set_edit_mode(true);
        set_is_visible(false);
    }

    function handle_split_click(e): void {
        e.preventDefault();
        props?.split_time_object(props.selected_chart_item);
    }

    function handle_delete_click(): void {
        props?.delete_time_object(parseInt(props.selected_chart_item));
        set_prompt_information({
            open: false,
            prompt_headline: "",
            prompt_text: "",
            on_accept_action: null,
            on_cancel_action: null
        });
    }

    function handle_align_left_click(e): void {
        e.preventDefault();
        props?.align_time_object(props.selected_chart_item, 'back');
    }

    function handle_align_right_click(e): void {
        e.preventDefault();
        props?.align_time_object(props.selected_chart_item, 'forward');
    }

    function handle_align_now_click(e): void {
        e.preventDefault();
        props?.align_time_object(props.selected_chart_item, 'now');
    }

    function handle_blur_click(e): void {
        if (context_menu_wrapper?.current?.contains(e.target)) {
            return;
        }

        set_is_visible(false);
        props?.set_selected_chart_item("");
    };

    function create_context_menu_classes(): string {
        let class_str: string = "Context_Menu_Wrapper";

        if (is_visible) {
            class_str += " Context_Menu_Wrapper--Active";
        }

        return class_str;
    }

    return (
        <div
            className={create_context_menu_classes()}
            style={{ top: props?.position?.top, left: props?.position?.left }}
            ref={context_menu_wrapper}
        >
            {
                timelog_object != null &&
                <>
                    <span className="Context_Menu_Customer">{timelog_object.customerName}</span>
                    <span className="Context_Menu_Project">{timelog_object.projectName}</span>
                    <span className="Context_Menu_Time">{t('Text.Clock')} {moment(timelog_object.startDate).format("HH:mm")} - {moment(timelog_object.endDate).format("HH:mm")}</span>
                </>
            }
            <nav className="Context_Menu">
                <ul className="Context_Menu_List">
                    <li key={generate_unique_id()} className="Context_Menu_List_Item">
                        <a
                            href="#"
                            className="Context_Menu_List_Item_Link"
                            onClick={(e) => handle_edit_mode_click(e)}
                            aria-label={t('Aria.EditObject')}
                            title={t('Aria.ClickToEditObject')}
                        >
                            <div className="Icon_Wrapper">
                                <Icon_Edit />
                            </div>
                            <span>{t('Button.Edit')}</span>
                        </a>
                    </li>
                    <li key={generate_unique_id()} className="Context_Menu_List_Item Context_Menu_List_Item_Split">
                        <a
                            href="#"
                            className="Context_Menu_List_Item_Link"
                            onClick={(e) => handle_align_left_click(e)}
                            aria-label={t('Aria.AlignObjectUp')}
                            title={t('Aria.ClickToAlignObjectUp')}
                        >
                            <div className="Icon_Wrapper">
                                <Icon_Arrow_Up />
                            </div>
                            <span>{t('Button.AdjustUp')}</span>
                        </a>
                        <a
                            href="#"
                            className="Context_Menu_List_Item_Link"
                            onClick={(e) => handle_align_right_click(e)}
                            aria-label={t('Aria.AlignObjectDown')}
                            title={t('Aria.ClickToAlignObjectDown')}
                        >
                            <div className="Icon_Wrapper">
                                <Icon_Arrow_Down />
                            </div>
                            <span>{t('Button.AdjustDown')}</span>
                        </a>
                    </li>
                    <li key={generate_unique_id()} className="Context_Menu_List_Item">
                        <a
                            href="#"
                            className="Context_Menu_List_Item_Link"
                            onClick={(e) => handle_align_now_click(e)}
                            aria-label={t('Aria.AlignObjectToNow')}
                            title={t('Aria.ClickToAlignObjectToNow')}
                        >
                            <div className="Icon_Wrapper">
                                <Icon_Clock />
                            </div>
                            <span>{t('Button.ToNow')}</span>
                        </a>
                    </li>
                    <li key={generate_unique_id()} className="Context_Menu_List_Item">
                        <a
                            href="#"
                            className="Context_Menu_List_Item_Link"
                            onClick={(e) => handle_split_click(e)}
                            aria-label={t('Aria.SplitObject')}
                            title={t('Aria.ClickToSplitObject')}
                        >
                            <div className="Icon_Wrapper">
                                <Icon_Split />
                            </div>
                            <span>{t('Button.Split')}</span>
                        </a>
                    </li>
                    <li key={generate_unique_id()} className="Context_Menu_List_Item">
                        <a
                            href="#"
                            className="Context_Menu_List_Item_Link"
                            onClick={(e) => {
                                e.preventDefault();
                                set_prompt_information({
                                    open: true,
                                    prompt_headline: t('Text.DeleteObject'),
                                    prompt_text: t('Text.AreYouSureDeleteObject'),
                                    on_accept_action: () => handle_delete_click(),
                                    on_cancel_action: () => set_prompt_information({
                                        open: false,
                                        prompt_headline: "",
                                        prompt_text: "",
                                        on_accept_action: null,
                                        on_cancel_action: null
                                    })
                                });
                            }}>
                            <div className="Icon_Wrapper">
                                <Icon_Trash />
                            </div>
                            <span>{t('Button.Delete')}</span>
                        </a>
                    </li>
                </ul>
            </nav>
        </div>
    );
}